/**
 * levelInputSetup — shared input wiring for a loaded level.
 *
 * Both the editor (play-mode) and the built player must set up input identically: load each
 * `input.InputReceiver`'s referenced `.input` asset, enable its default action maps, and — when a level
 * has NO InputReceiver — fall back to a built-in "Gameplay" map (WASD/Arrows + Space + pointer) so
 * scripts/templates work out of the box. Keeping the default map + the algorithm here is the single
 * source of truth, so editor and player can't drift (a drift bug is exactly why input worked in the
 * editor but not the player).
 */

import type { InputManager } from './InputManager';
import type { InputAssetDef } from './types';

/** Serialized data shape of an `input.InputReceiver` component (subset this module needs). */
export interface InputReceiverData {
  inputAsset?: string | null;
  defaultActionMaps?: string[];
  activateOnEnable?: boolean;
}

/** Minimal level surface needed to discover InputReceiver components (avoids a hard @engine/core type dep). */
export interface LevelInputSource {
  getAllObjects(): Iterable<{ readonly guid: string; getComponentData(typeId: string): unknown }>;
}

/** One activated receiver: the entity (GUID) that should get action events + its activated maps. */
export interface InputReceiverBinding {
  guid: string;
  maps: string[];
}

/**
 * Built-in default action map, loaded when a level declares no `input.InputReceiver`. Mirrors the set of
 * actions the engine's templates expect (movement on WASD/Arrows, Jump/Fire on Space, mouse Pointer).
 */
export const DEFAULT_GAMEPLAY_INPUT: InputAssetDef = {
  version: 1,
  controlSchemes: [{ name: 'Keyboard', devices: ['keyboard'], bindingGroups: ['Keyboard'] }],
  actionMaps: [
    {
      name: 'Gameplay',
      actions: [
        {
          name: 'Action1',
          type: 'axis2d',
          bindings: [
            { path: '<Keyboard>/KeyW', group: 'wasd', compositePart: 'up' },
            { path: '<Keyboard>/KeyS', group: 'wasd', compositePart: 'down' },
            { path: '<Keyboard>/KeyA', group: 'wasd', compositePart: 'left' },
            { path: '<Keyboard>/KeyD', group: 'wasd', compositePart: 'right' },
            { path: '<Keyboard>/ArrowUp', group: 'arrows', compositePart: 'up' },
            { path: '<Keyboard>/ArrowDown', group: 'arrows', compositePart: 'down' },
            { path: '<Keyboard>/ArrowLeft', group: 'arrows', compositePart: 'left' },
            { path: '<Keyboard>/ArrowRight', group: 'arrows', compositePart: 'right' },
          ],
        },
        {
          name: 'Move',
          type: 'axis2d',
          bindings: [
            { path: '<Keyboard>/KeyW', group: 'wasd', compositePart: 'up' },
            { path: '<Keyboard>/KeyS', group: 'wasd', compositePart: 'down' },
            { path: '<Keyboard>/KeyA', group: 'wasd', compositePart: 'left' },
            { path: '<Keyboard>/KeyD', group: 'wasd', compositePart: 'right' },
            { path: '<Keyboard>/ArrowUp', group: 'arrows', compositePart: 'up' },
            { path: '<Keyboard>/ArrowDown', group: 'arrows', compositePart: 'down' },
            { path: '<Keyboard>/ArrowLeft', group: 'arrows', compositePart: 'left' },
            { path: '<Keyboard>/ArrowRight', group: 'arrows', compositePart: 'right' },
          ],
        },
        { name: 'Jump', type: 'button', bindings: [{ path: '<Keyboard>/Space' }] },
        { name: 'Fire', type: 'button', bindings: [{ path: '<Keyboard>/Space' }] },
        { name: 'Pointer', type: 'axis2d', bindings: [{ path: '<Mouse>/position' }] },
      ],
    },
  ],
};

/**
 * Wire a level's input into `manager`.
 *
 * @param manager       the InputManager to load assets / enable maps into
 * @param level         the loaded level (scanned for `input.InputReceiver` components)
 * @param loadAssetDef  resolves an input-asset GUID to its parsed {@link InputAssetDef}, or null if missing
 *                      (editor reads VFS; player reads the build manifest)
 * @returns the activated receivers (entity GUID + action maps) — feed to {@link routeLevelInputEvents}
 *          to deliver action phase events to those entities' scripts
 */
export async function setupLevelInput(
  manager: InputManager,
  level: LevelInputSource,
  loadAssetDef: (guid: string) => Promise<InputAssetDef | null>,
): Promise<InputReceiverBinding[]> {
  const receivers: InputReceiverBinding[] = [];
  let receiverCount = 0;

  for (const go of level.getAllObjects()) {
    const ir = go.getComponentData('input.InputReceiver') as InputReceiverData | null;
    if (!ir?.inputAsset) continue;
    receiverCount++;

    try {
      const def = await loadAssetDef(ir.inputAsset);
      if (def) manager.loadAsset(def);
    } catch {
      // A missing/broken input asset must not abort level load — other receivers still set up.
    }

    if (ir.activateOnEnable !== false && ir.defaultActionMaps?.length) {
      for (const mapName of ir.defaultActionMaps) manager.enableActionMap(mapName);
      receivers.push({ guid: go.guid, maps: [...ir.defaultActionMaps] });
    }
  }

  // No InputReceiver in the level → load the built-in Gameplay map so input still works.
  if (receiverCount === 0) {
    manager.loadAsset(DEFAULT_GAMEPLAY_INPUT);
    manager.enableActionMap('Gameplay');
  }

  return receivers;
}

/**
 * Subscribe each receiver's activated action maps to their actions' phase callbacks
 * (`started`/`performed`/`canceled`) and forward every transition to the receiver's entity —
 * the event-driven counterpart to per-frame polling ("don't poll bindings every frame; the
 * action system already emits events"). The host routes `events.inputAction` through its
 * script subsystem's `triggerEvent`, so a TS behaviour receives it as
 * `onInputAction({ action, map, phase, value, control, device, duration })` — the same
 * delivery path physics collision events use.
 *
 * Call AFTER {@link setupLevelInput} (actions must exist in the manager). Returns a single
 * unsubscribe — call it on level unload / exit play mode, or stale subscriptions from the
 * previous session keep firing into destroyed scripts.
 *
 * @param dispatch host bridge to the script runtime, e.g.
 *                 `(guid, id, params) => scriptSubsystem.triggerEvent(guid, id, params)`
 */
export function routeLevelInputEvents(
  manager: InputManager,
  receivers: readonly InputReceiverBinding[],
  dispatch: (receiverGuid: string, eventId: string, params: Record<string, unknown>) => void,
): () => void {
  const unsubs: Array<() => void> = [];

  for (const receiver of receivers) {
    for (const mapName of receiver.maps) {
      const map = manager.getActionMap(mapName);
      if (!map) continue;
      for (const action of map) {
        // ctx.phase is already set to the transition phase by InputAction, so one forwarder
        // serves all three callback lists.
        const forward = (ctx: {
          action: string;
          phase: string;
          value: unknown;
          control: string;
          device: string;
          duration: number;
        }): void => {
          dispatch(receiver.guid, 'events.inputAction', {
            action: ctx.action,
            map: mapName,
            phase: ctx.phase,
            value: ctx.value,
            control: ctx.control,
            device: ctx.device,
            duration: ctx.duration,
          });
        };
        unsubs.push(action.onStarted(forward), action.onPerformed(forward), action.onCanceled(forward));
      }
    }
  }

  return () => {
    for (const unsub of unsubs) unsub();
    unsubs.length = 0;
  };
}
